import React, { useState } from 'react';
import { Edit, Trash2, Tag, Link2, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const HAZIDWorksheet = ({ searchQuery, filterLocation, filterRiskLevel, onEquipmentTag }) => {
  const [selectedRows, setSelectedRows] = useState([]);

  const hazidEntries = [
    {
      id: 'HAZID-001',
      node: 'Compression System',
      hazard: 'High Pressure Gas Release',
      cause: 'Seal failure in compressor',
      consequence: 'Personnel injury, equipment damage',
      safeguards: ['Emergency shutdown system', 'Pressure relief valve', 'Gas detection'],
      riskLevel: 'high',
      probability: 'C',
      severity: '4',
      equipment: ['COMP-01', 'PSV-101'],
      location: 'offshore-alpha',
      status: 'active',
      linkedPermits: ['PTW-2024-156'],
    },
    {
      id: 'HAZID-002',
      node: 'Storage Tank Farm',
      hazard: 'Flammable Liquid Spillage',
      cause: 'Tank overfill due to level transmitter failure',
      consequence: 'Fire, environmental contamination',
      safeguards: ['High level alarm', 'Independent overfill protection', 'Bund containment'],
      riskLevel: 'critical',
      probability: 'D',
      severity: '5',
      equipment: ['TK-201', 'LT-201'],
      location: 'refinery-01',
      status: 'pending-review',
      linkedPermits: [],
    },
    {
      id: 'HAZID-003',
      node: 'Process Heater',
      hazard: 'Tube Rupture',
      cause: 'Corrosion, thermal stress',
      consequence: 'Fire, explosion risk',
      safeguards: ['Temperature monitoring', 'Pressure monitoring', 'Emergency isolation'],
      riskLevel: 'high',
      probability: 'C',
      severity: '4',
      equipment: ['HTR-01'],
      location: 'offshore-alpha',
      status: 'active',
      linkedPermits: ['PTW-2024-178'],
    },
  ];

  const filteredEntries = hazidEntries?.filter(entry => {
    const matchesSearch = searchQuery === '' || 
      entry?.node?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      entry?.hazard?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      entry?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesLocation = filterLocation === 'all' || entry?.location === filterLocation;
    const matchesRisk = filterRiskLevel === 'all' || entry?.riskLevel === filterRiskLevel;
    
    return matchesSearch && matchesLocation && matchesRisk;
  });

  const getRiskBadgeColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200',
    };
    return colors?.[level] || colors?.medium;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Active', color: 'bg-green-100 text-green-800' },
      'pending-review': { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
      archived: { label: 'Archived', color: 'bg-gray-100 text-gray-800' },
    };
    return badges?.[status] || badges?.active;
  };

  return (
    <div className="space-y-6">
      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID / Node
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hazard Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cause
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Safeguards
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Equipment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEntries?.map((entry) => (
                <tr key={entry?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{entry?.id}</div>
                    <div className="text-xs text-gray-500">{entry?.node}</div>
                    <span className={`inline-flex mt-1 px-2 py-1 text-xs font-medium rounded ${getStatusBadge(entry?.status)?.color}`}>
                      {getStatusBadge(entry?.status)?.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{entry?.hazard}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">{entry?.cause}</div>
                  </td>
                  <td className="px-6 py-4">
                    <ul className="text-xs text-gray-600 space-y-1">
                      {entry?.safeguards?.map((safeguard, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" />
                          {safeguard}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium border ${getRiskBadgeColor(entry?.riskLevel)}`}>
                      {entry?.riskLevel?.toUpperCase()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      P: {entry?.probability} | S: {entry?.severity}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {entry?.equipment?.map((equip, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                          {equip}
                        </span>
                      ))}
                    </div>
                    {entry?.linkedPermits?.length > 0 && (
                      <div className="flex items-center mt-1 text-xs text-green-600">
                        <Link2 className="w-3 h-3 mr-1" />
                        {entry?.linkedPermits?.length} Permit(s)
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onEquipmentTag(entry?.equipment)}
                      >
                        <Tag className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredEntries?.map((entry) => (
          <div key={entry?.id} className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-gray-900">{entry?.id}</div>
                <div className="text-sm text-gray-600">{entry?.node}</div>
              </div>
              <span className={`px-2.5 py-1 rounded text-xs font-medium ${getRiskBadgeColor(entry?.riskLevel)}`}>
                {entry?.riskLevel?.toUpperCase()}
              </span>
            </div>

            <div className="space-y-2">
              <div>
                <div className="text-xs font-medium text-gray-500">Hazard</div>
                <div className="text-sm text-gray-900">{entry?.hazard}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500">Cause</div>
                <div className="text-sm text-gray-600">{entry?.cause}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1">
              {entry?.equipment?.map((equip, idx) => (
                <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                  {equip}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(entry?.status)?.color}`}>
                {getStatusBadge(entry?.status)?.label}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => onEquipmentTag(entry?.equipment)}>
                  <Tag className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost">
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HAZIDWorksheet;